import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ArrowLeft, Ban, Gavel, Search, ShieldAlert, TriangleAlert, Undo2 } from 'lucide-react';
import {
  MODERATION,
  MODERATION_RULES,
  RESTRICTIONS,
  type AdminAction,
  type AdminUserDetail,
  type AdminUserSummary,
  type ModerationActionType,
  type ModerationRule,
  type Restriction,
  type StaffRole,
} from '@spawnwire/shared';
import { Avatar, Button, clsx, Field, Input, Modal, Select, Spinner, TextArea } from '../../components/ui';
import { errorMessage, http } from '../../lib/api';
import { actionTitle, formatDate, formatHours, RESTRICTION_HINT, RESTRICTION_LABEL, RULE_LABEL, statusLine } from '../../lib/moderation';
import { useStore } from '../../store/store';
import { ui } from '../../store/ui';
import { STATE_LABEL } from '../minecraft/MinecraftPanels';
import { Card, Empty, LevelBadge, PageHeader, ReasonDialog, StaffBadge, Tabs } from './adminUi';
import { loadAdminStats } from './store';

type Filter = 'all' | 'staff' | 'banned' | 'restricted';

export function UsersPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [rows, setRows] = useState<AdminUserSummary[] | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (offset = 0) => {
      try {
        const res = await http.get<{ users: AdminUserSummary[]; hasMore: boolean }>(`/admin/users?q=${encodeURIComponent(q)}&filter=${filter}&offset=${offset}`);
        setRows((prev) => (offset && prev ? [...prev, ...res.users] : res.users));
        setHasMore(res.hasMore);
        setError(null);
      } catch (err) {
        setError(errorMessage(err));
      }
    },
    [q, filter],
  );

  useEffect(() => {
    const t = setTimeout(() => void load(), 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div>
      <PageHeader title="Users" description="Search everyone on Spawnwire by username, display name, email or ID." />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative min-w-60 flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-faint" aria-hidden />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search users" className="pl-9" aria-label="Search users" />
        </div>
        <Tabs
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'all', label: 'All' },
            { value: 'banned', label: 'Banned' },
            { value: 'restricted', label: 'Restricted' },
            { value: 'staff', label: 'Staff' },
          ]}
        />
      </div>
      {error && <p className="mb-3 text-sm text-danger">{error}</p>}
      {!rows ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <Empty>No one matches.</Empty>
      ) : (
        <ul className="divide-y divide-line overflow-hidden rounded-xl border border-line bg-sidebar">
          {rows.map((r) => (
            <li key={r.user.id}>
              <button type="button" onClick={() => navigate(r.user.id)} className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-hover">
                <Avatar user={r.user} size={36} />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="truncate font-medium text-fg">{r.user.displayName}</span>
                    <StaffBadge role={r.staffRole} />
                  </span>
                  <span className="block truncate text-xs text-muted">
                    @{r.user.username}
                    {r.email && ` · ${r.email}`}
                  </span>
                </span>
                <span className="hidden text-right text-xs text-faint md:block">
                  Joined {new Date(r.user.createdAt).toLocaleDateString()}
                  <br />
                  {r.lastActiveAt ? `Active ${new Date(r.lastActiveAt).toLocaleDateString()}` : 'Never signed in'}
                </span>
                <LevelBadge level={r.level} />
              </button>
            </li>
          ))}
        </ul>
      )}
      {hasMore && rows && (
        <Button variant="secondary" className="mt-3 w-full" onClick={() => void load(rows.length)}>
          Load more
        </Button>
      )}
    </div>
  );
}

const DURATIONS: { value: string; label: string; hours: number | null }[] = [
  { value: '24', label: '1 day', hours: 24 },
  { value: '72', label: '3 days', hours: 72 },
  { value: '168', label: '7 days', hours: 168 },
  { value: 'custom', label: 'Custom length', hours: 0 },
  { value: 'permanent', label: 'Permanent', hours: null },
];

/** Issues a warning, ban or restriction. Opened from a user's page or from a report. */
export function ActionDialog({
  user,
  initialType = 'warning',
  reportId,
  onClose,
  onDone,
}: {
  user: { id: string; displayName: string; username: string };
  initialType?: ModerationActionType;
  reportId?: string;
  onClose: () => void;
  onDone: (action: AdminAction) => void;
}) {
  const isAdmin = useStore((s) => s.me?.staffRole === 'admin');
  const [type, setType] = useState<ModerationActionType>(initialType);
  const [restriction, setRestriction] = useState<Restriction>('messaging');
  const [rule, setRule] = useState<ModerationRule>('spam');
  const [duration, setDuration] = useState('24');
  const [customAmount, setCustomAmount] = useState('2');
  const [customUnit, setCustomUnit] = useState<'hours' | 'days'>('days');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const durations = DURATIONS.filter((d) => isAdmin || d.value !== 'permanent');
  const hours =
    duration === 'custom' ? Math.round(Number(customAmount) * (customUnit === 'days' ? 24 : 1)) : (durations.find((d) => d.value === duration)?.hours ?? null);
  const tooLong = type !== 'warning' && !isAdmin && (hours === null || hours > MODERATION.moderatorMaxHours);
  const badCustom = duration === 'custom' && (!Number.isFinite(hours) || (hours ?? 0) < 1 || (hours ?? 0) > MODERATION.maxHours);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const action = await http.post<AdminAction>(`/admin/users/${user.id}/actions`, {
        type,
        restriction: type === 'restriction' ? restriction : null,
        rule,
        reason: reason.trim(),
        staffNote: note.trim(),
        durationHours: type === 'warning' ? null : hours,
        reportId: reportId ?? null,
      });
      ui.toast(`${actionTitle(action)} given to @${user.username}.`, 'success');
      void loadAdminStats();
      onDone(action);
      onClose();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={`Take action on @${user.username}`} description="They see the rule and reason in their account standing. The staff note stays private." size="lg">
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Action">
          {(
            [
              ['warning', 'Warning', TriangleAlert],
              ['restriction', 'Restriction', ShieldAlert],
              ['ban', 'Ban', Gavel],
            ] as const
          ).map(([value, label, Icon]) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={type === value}
              onClick={() => setType(value)}
              className={clsx(
                'flex flex-col items-center gap-1 rounded-lg border px-3 py-3 text-sm font-semibold transition-colors',
                type === value ? (value === 'ban' ? 'border-danger bg-danger/10 text-fg' : 'border-accent bg-accent-soft text-fg') : 'border-line bg-main text-muted hover:text-fg',
              )}
            >
              <Icon className="size-5" aria-hidden />
              {label}
            </button>
          ))}
        </div>

        {type === 'restriction' && (
          <Field label="Restrict" hint={RESTRICTION_HINT[restriction]}>
            <Select value={restriction} onChange={setRestriction} options={RESTRICTIONS.map((r) => ({ value: r, label: RESTRICTION_LABEL[r] }))} />
          </Field>
        )}

        <Field label="Rule broken">
          <Select value={rule} onChange={setRule} options={MODERATION_RULES.map((r) => ({ value: r, label: RULE_LABEL[r] }))} />
        </Field>

        {type === 'warning' ? (
          <p className="text-sm text-muted">Warnings count toward their standing for {MODERATION.warningActiveDays} days.</p>
        ) : (
          <Field label="Length" hint={isAdmin ? undefined : `Moderators can give up to ${MODERATION.moderatorMaxHours / 24} days. Ask an admin for longer.`}>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Select value={duration} onChange={setDuration} options={durations.map((d) => ({ value: d.value, label: d.label }))} className="sm:w-48" />
              {duration === 'custom' && (
                <div className="flex gap-2">
                  <Input type="number" min={1} value={customAmount} onChange={(e) => setCustomAmount(e.target.value)} className="w-24" aria-label="Length" />
                  <Select
                    value={customUnit}
                    onChange={setCustomUnit}
                    options={[
                      { value: 'hours', label: 'hours' },
                      { value: 'days', label: 'days' },
                    ]}
                    className="w-28"
                  />
                </div>
              )}
            </div>
          </Field>
        )}

        <Field label="Reason (shown to them)">
          <TextArea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} placeholder="e.g. Sending the same invite link to many people." aria-label="Reason shown to them" />
        </Field>
        <Field label="Staff note (private, optional)">
          <TextArea rows={2} value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} placeholder="Links, context, message IDs…" aria-label="Private staff note" />
        </Field>

        {type === 'ban' && <p className="rounded-lg bg-danger/10 p-3 text-sm text-fg">They are signed out of the app straight away, their game servers are stopped, and they see a ban screen with this reason until it ends.</p>}
        {error && <p className="text-sm text-danger">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant={type === 'ban' ? 'danger' : 'primary'} loading={busy} disabled={reason.trim().length < 3 || tooLong || badCustom}>
            {type === 'warning' ? 'Give warning' : type === 'ban' ? `Ban ${hours === null ? 'permanently' : `for ${formatHours(hours)}`}` : `Restrict ${hours === null ? 'permanently' : `for ${formatHours(hours)}`}`}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function ActionRow({ action, onRevoked }: { action: AdminAction; onRevoked: (a: AdminAction) => void }) {
  const [revoking, setRevoking] = useState(false);
  return (
    <li className="rounded-xl border border-line bg-main p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={clsx(
            'rounded-full px-2 py-0.5 text-xs font-semibold',
            !action.active ? 'bg-active text-muted' : action.type === 'ban' ? 'bg-danger text-white' : action.type === 'restriction' ? 'bg-[#f97316] text-white' : 'bg-warning text-black',
          )}
        >
          {actionTitle(action)}
        </span>
        <span className="text-sm font-medium text-fg">{RULE_LABEL[action.rule]}</span>
        <span className="ml-auto text-xs text-faint">{formatDate(action.createdAt)}</span>
      </div>
      <p className="mt-2 whitespace-pre-wrap text-sm text-fg">{action.reason}</p>
      {action.staffNote && <p className="mt-1 whitespace-pre-wrap rounded bg-sidebar p-2 text-xs text-muted">Staff note: {action.staffNote}</p>}
      <p className="mt-2 text-xs text-muted">
        {statusLine(action)} · by {action.moderator ? `@${action.moderator.username}` : 'unknown'}
        {action.revokedBy && ` · lifted by @${action.revokedBy.username}${action.revokeReason ? `: ${action.revokeReason}` : ''}`}
      </p>
      {action.appeal && (
        <p className="mt-2 text-xs text-muted">
          Appeal ({action.appeal.status}): “{action.appeal.message}”{action.appeal.response && ` → ${action.appeal.response}`}
        </p>
      )}
      {action.active && (
        <Button size="sm" variant="secondary" icon={Undo2} className="mt-3" onClick={() => setRevoking(true)}>
          Lift
        </Button>
      )}
      {revoking && (
        <ReasonDialog
          title={`Lift this ${action.type}?`}
          description="It stops counting straight away. They see that it was lifted, with your reason."
          confirmLabel="Lift"
          placeholder="e.g. Issued by mistake."
          onClose={() => setRevoking(false)}
          onSubmit={async (reason) => {
            const updated = await http.post<AdminAction>(`/admin/actions/${action.id}/revoke`, { reason });
            onRevoked(updated);
            void loadAdminStats();
          }}
        />
      )}
    </li>
  );
}

export function UserDetailPage() {
  const { userId = '' } = useParams();
  const navigate = useNavigate();
  const me = useStore((s) => s.me);
  const [detail, setDetail] = useState<AdminUserDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<ModerationActionType | null>(null);

  const load = useCallback(async () => {
    try {
      setDetail(await http.get<AdminUserDetail>(`/admin/users/${userId}`));
      setError(null);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [userId]);

  useEffect(() => {
    setDetail(null);
    void load();
  }, [load]);

  if (!detail) return error ? <p className="text-sm text-danger">{error}</p> : <Spinner />;
  const { user } = detail;
  const self = user.id === me?.id;
  const rank = (r: StaffRole | null | undefined) => (r === 'admin' ? 2 : r === 'moderator' ? 1 : 0);
  const canModerate = !self && rank(detail.staffRole) < rank(me?.staffRole);

  return (
    <div className="space-y-6">
      <button type="button" onClick={() => navigate('/app/admin/users')} className="flex items-center gap-1.5 text-sm font-medium text-muted hover:text-fg">
        <ArrowLeft className="size-4" aria-hidden /> Users
      </button>

      <Card className="p-5">
        <div className="flex flex-wrap items-start gap-4">
          <Avatar user={user} size={64} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate text-xl font-bold text-fg">{user.displayName}</h1>
              <StaffBadge role={detail.staffRole} />
              <LevelBadge level={detail.level} />
            </div>
            <p className="text-sm text-muted">
              @{user.username}
              {detail.email && ` · ${detail.email}`}
            </p>
            <p className="mt-1 text-xs text-faint">
              {detail.online ? <span className="text-success">Online now</span> : detail.lastActiveAt ? `Last active ${formatDate(detail.lastActiveAt)}` : 'Never signed in'} · Joined {formatDate(user.createdAt)} · ID {user.id}
            </p>
          </div>
        </div>
        {canModerate && (
          <div className="mt-4 flex flex-wrap gap-2">
            <Button icon={TriangleAlert} variant="secondary" onClick={() => setDialog('warning')}>
              Warn
            </Button>
            <Button icon={ShieldAlert} variant="secondary" onClick={() => setDialog('restriction')}>
              Restrict
            </Button>
            <Button icon={Ban} variant="danger" onClick={() => setDialog('ban')}>
              Ban
            </Button>
          </div>
        )}
        {me?.staffRole === 'admin' && !self && <RoleControl detail={detail} onChange={setDetail} />}
      </Card>

      <section>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted">Moderation history</h2>
        {detail.actions.length === 0 ? (
          <Empty>No warnings or actions.</Empty>
        ) : (
          <ul className="space-y-2">
            {detail.actions.map((a) => (
              <ActionRow key={a.id} action={a} onRevoked={() => void load()} />
            ))}
          </ul>
        )}
      </section>

      <section>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted">Reports against them</h2>
        {detail.reports.length === 0 ? (
          <Empty>No reports.</Empty>
        ) : (
          <ul className="space-y-2">
            {detail.reports.map((r) => (
              <li key={r.id} className="rounded-xl border border-line bg-sidebar p-3 text-sm">
                <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
                  <span className="font-semibold uppercase text-fg">{r.category.replace('_', ' ')}</span>
                  <span>· {r.status}</span>
                  <span>· by @{r.reporter?.username ?? 'unknown'}</span>
                  <span className="ml-auto">{formatDate(r.createdAt)}</span>
                </div>
                {r.messageContent && <p className="mt-1 whitespace-pre-wrap border-l-2 border-line pl-2 text-fg">{r.messageContent}</p>}
                {r.reason && <p className="mt-1 text-muted">{r.reason}</p>}
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className="grid gap-6 md:grid-cols-2">
        <section>
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted">Game servers</h2>
          {detail.servers.length === 0 ? (
            <Empty>None.</Empty>
          ) : (
            <ul className="space-y-2">
              {detail.servers.map((s) => (
                <li key={s.server.id}>
                  <button type="button" onClick={() => navigate(`/app/admin/servers?q=${encodeURIComponent(s.server.name)}`)} className="w-full rounded-xl border border-line bg-sidebar p-3 text-left text-sm hover:bg-hover">
                    <span className="font-medium text-fg">{s.server.name}</span>
                    <span className="block text-xs text-muted">
                      {s.server.address} · {STATE_LABEL[s.server.state]}
                      {s.suspendedAt && ' · suspended'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
        <section>
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted">Spaces they own</h2>
          {detail.ownedSpaces.length === 0 ? (
            <Empty>None.</Empty>
          ) : (
            <ul className="space-y-2">
              {detail.ownedSpaces.map((s) => (
                <li key={s.id} className="rounded-xl border border-line bg-sidebar p-3 text-sm">
                  <span className="font-medium text-fg">{s.name}</span>
                  <span className="block text-xs text-muted">{s.memberCount} members</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {dialog && <ActionDialog user={user} initialType={dialog} onClose={() => setDialog(null)} onDone={() => void load()} />}
    </div>
  );
}

function RoleControl({ detail, onChange }: { detail: AdminUserDetail; onChange: (d: AdminUserDetail) => void }) {
  const [busy, setBusy] = useState(false);
  async function set(role: StaffRole | 'none') {
    setBusy(true);
    try {
      const summary = await http.put<AdminUserSummary>(`/admin/users/${detail.user.id}/role`, { role: role === 'none' ? null : role });
      onChange({ ...detail, staffRole: summary.staffRole });
      ui.toast(role === 'none' ? `@${detail.user.username} is no longer staff.` : `@${detail.user.username} is now ${role === 'admin' ? 'an admin' : 'a moderator'}.`, 'success');
    } catch (err) {
      ui.toast(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-line pt-4">
      <span className="text-sm text-muted">Staff role</span>
      <Select
        value={detail.staffRole ?? 'none'}
        onChange={(v) => void set(v)}
        options={[
          { value: 'none', label: 'None' },
          { value: 'moderator', label: 'Moderator' },
          { value: 'admin', label: 'Admin' },
        ]}
        className="w-40"
      />
      {busy && <Spinner className="size-4" />}
      <span className="text-xs text-faint">Moderators can warn, restrict and ban for up to 7 days. Admins can do everything.</span>
    </div>
  );
}
